import { authFetch, BASE_BACKEND_URL } from '../auth'
import React, { useEffect, useRef, useState, useContext } from 'react'
import dayjs from '../utils/dayjsConfig'
import { detectChangesLog } from '../route/helpers/logsRouteUtils'
import { mapRoutePlanError } from '../route/helpers/routePlanErrorMessages'

import cookie from 'react-cookies'
import { Table } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { MetroSpinner } from 'react-spinners-kit'
import { AsyncPaginate } from 'react-select-async-paginate'
import CheckboxDropdownList from './Components/CheckboxDropdownList'
import ClientsInRoutePlanList from './Components/ClientsInRoutePlanList'
import ActionButtonProvider from '../components/action_button/ActionButtonContext'
import UnavailableUserClientModal from './Components/UnavailableUserClientModal'
import { sortClients } from '../route/helpers/utils'

const CreateNewRoutePlan = () => {
  const [saving, setSaving] = useState(false)
  const [readyToCollect, setReadyToCollect] = useState(false)
  const [failedCreation, setFailedCreation] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [routeData, setRouteData] = useState(null)
  const [copyRoute, setCopyRoute] = useState(false)
  const [labelToIdMapping, setLabelToIdMapping] = useState({})

  const [title, setTitle] = useState('')
  const [routeUid, setRouteUid] = useState(null)
  const [users, setUsers] = useState([])
  const [occupiedUsersData, setOccupiedUsersData] = useState(null)
  const [userData, setUserData] = useState(null)
  const [userOptions, setUserOptions] = useState(null)
  const [clients, setClients] = useState([])
  const [unavailableClients, setUnavailableClients] = useState(null)
  const [unavailableUsers, setUnavailableUsers] = useState(null)
  const [showUnavailableModal, setShowUnavailableModal] = useState(false)
  const baseClientsRef = useRef([])
  const baseUsersRef = useRef([])

  let abortController = new AbortController()
  let abortClientController = new AbortController()

  const abortCurrentFetchs = () => {
    abortController.abort('Fetch Aborted by User.')
    abortClientController.abort('Fetch Aborted by User.')
  }

  const abortClientFetchs = () => {
    abortClientController.abort('Fetch Aborted by User.')
  }

  useEffect(() => {
    window.addEventListener('beforeunload', abortCurrentFetchs)
    return () => {
      window.removeEventListener('beforeunload', abortCurrentFetchs)
      abortCurrentFetchs()
    }
  }, [])

  const normalizeRouteTitle = (value) => (value || '').trim()

  const handleTitleChange = (event) => {
    setTitle(event.target.value)
  }

  const updateSelectedUsers = (selectedLabels) => {
    if (selectedLabels.length <= 0) {
      setClients([])
    }
    const labels = Array.isArray(selectedLabels) ? selectedLabels : Array.from(selectedLabels || [])
    const selectedIds = labels.map((label) => labelToIdMapping[label])
    const selectedUserData = userData.filter((user) => selectedIds.includes(user.uid))
    setUsers(selectedUserData)
  }

  const handleDeleteUser = (userUid) => {
    const remainingUsers = users.filter((user) => userUid !== user.uid)
    setUsers(remainingUsers)
  }

  const handleClientOption = (options, actionMeta) => {
    if (actionMeta?.action === "select-option") {
      const now = dayjs().toISOString()
      const updatedOptions = options.map((c) => ({
        ...c,
        created_at: c.created_at || now
      }))
      setClients(updatedOptions)
    }
  }

  const handleDeleteClient = (clientValue) => {
    const remainingClients = clients.filter((client) => clientValue !== client.value)
    setClients(remainingClients)
  }

  const handleChangeStartDate = (clientValue, startDate) => {
    const changedClients = clients.map((c) => {
      if (c.value === clientValue) {
        return { ...c, startDate: startDate }
      }
      return c
    })
    setClients(changedClients)
  }

  const handleChangeVisitDays = (clientValue, visitDays) => {
    const normalizedVisitDays = Array.isArray(visitDays) ? visitDays : []
    const visitDayValues = normalizedVisitDays.map((day) => {
      return day.value
    })
    const changedClients = clients.map((c) => {
      if (c.value === clientValue) {
        return { ...c, visitDays: visitDayValues, selectedWeekDays: normalizedVisitDays }
      }
      return c
    })
    setClients(changedClients)
  }

  const handleChangeRecurrence = (clientValue, recurrenceType, recurrenceAmount) => {
    const type = parseInt(recurrenceType, 10)
    const amount = parseInt(recurrenceAmount, 10)
    if (!Number.isFinite(type) || !Number.isFinite(amount) || amount < 1) {
      return
    }
    const recurrenceTotal = amount * type
    if (!Number.isFinite(recurrenceTotal) || recurrenceTotal < 7) {
      return
    }
    const changedClients = clients.map((c) => {
      if (c.value === clientValue) {
        return {
          ...c,
          recurrence: recurrenceTotal,
          recurrenceType: recurrenceType,
          recurrenceAmount: recurrenceAmount
        }
      }
      return c
    })
    setClients(changedClients)
  }

  const handleCloseAlert = (e) => {
    e.preventDefault()
    setFailedCreation(false)
  }

  const getClient = async (inputValue, loadedOptions, { page }) => {
    if (inputValue.length < 3) {
      return { options: [], hasMore: false, additional: { page: 1 } }
    }

    abortClientController = new AbortController()

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/clients?q=${inputValue}&active=true&page=${page}&per_page=25`,
        {
          method: 'GET',
          signal: abortClientController.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      const data = await response.json()

      const options = (data.table || []).map((cl) => ({
        value: cl.db_ref,
        label: `${cl.db_ref} - ${cl.name}`,
        startDate: '',
        visitDays: [],
        selectedWeekDays: [],
        recurrence: 7,
        recurrenceType: 7,
        recurrenceAmount: 1,
        client: cl
      }))

      return {
        options,
        hasMore: page * 25 < (data.count || 0),
        additional: { page: page + 1 }
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error fetching clients:', error)
      }
      return { options: [], hasMore: false, additional: { page: 1 } }
    }
  }

  const getClientsForCopy = async (clientsInfo, routeClients, editing) => {
    abortClientController = new AbortController()
    let body = {
      client_uids: clientsInfo
    }
    return authFetch(
      `${BASE_BACKEND_URL}/route_plans/?clients_copy=true&businesses=${cookie.load('business_id')}`,
      {
        method: 'POST',
        signal: abortClientController.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (data) {
          if (data?.unavailable_clients?.length > 0) {
            setFailedCreation(
              `Al intentar crear la ${editing ? 'edicion' : 'copia'} del plan de visitas, algunos clientes no pudieron ser agregados porque actualmente no están disponibles.`
            )
            setTimeout(() => setFailedCreation(false), 15000)
          }
          let foundClients = []
          let clientsInData = data.clients
          for (const client of clientsInData) {
            const clientInRoute = routeClients.find((c) => c.value === client.db_ref)
            if (clientInRoute) {
              foundClients.push({
                value: client.db_ref,
                label: `${client.db_ref} - ${client.name}`,
                startDate: editing ? clientInRoute.startDate : '',
                visitDays: clientInRoute.visitDays,
                selectedWeekDays: clientInRoute.selectedWeekDays,
                recurrence: clientInRoute.recurrence,
                recurrenceType: clientInRoute.recurrenceType,
                recurrenceAmount: clientInRoute.recurrenceAmount,
                created_at: clientInRoute.created_at || dayjs().toISOString(),
                client: client
              })
            }
          }
          setClients(sortClients(foundClients))
        } else {
          setFailedCreation(
            'Ocurrió un error inesperado. Contacte a soporte si el problema persiste.'
          )
          return setTimeout(() => setFailedCreation(false), 15000)
        }
      })
      .catch((error) => {
        if (error !== 'Fetch Aborted by User.') {
          console.error('Error fetching clients:', error)
          return []
        }
      })
  }

  const getUsers = () => {
    abortController = new AbortController()
    authFetch(`${BASE_BACKEND_URL}/ubiqua_app_users?non_paginated=true`, {
      method: 'GET',
      signal: abortController.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        const localLabelToIdMapping = {}
        const occupiedUsers = {}
        const dropdownOptions = data.table.map((user) => {
          const displayText = `${user.uid} - ${user.full_name}`
          localLabelToIdMapping[displayText] = user.uid
          if (user.route_plan_uid !== null) {
            occupiedUsers[displayText] = user.route_plan_title
          }
          return displayText
        })
        setOccupiedUsersData(occupiedUsers)
        setLabelToIdMapping(localLabelToIdMapping)
        setUserOptions(dropdownOptions)
        setUserData(data.table)
      })
      .catch((error) => {
        if (error !== 'Fetch Aborted by User.') {
          console.error('Error:', error)
        }
      })
  }

  const getUsersForEdit = async (usersInfo) => {
    abortController = new AbortController()
    let body = {
      user_uids: usersInfo
    }
    return authFetch(
      `${BASE_BACKEND_URL}/route_plans/?users_copy=true&businesses=${cookie.load('business_id')}`,
      {
        method: 'POST',
        signal: abortController.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (data) {
          if (data?.unavailable_users?.length > 0) {
            setFailedCreation(
              'Al intentar crear la edicion del plan de visitas, algunos usuarios no pudieron ser agregados porque actualmente no están disponibles.'
            )
            setTimeout(() => setFailedCreation(false), 15000)
          }
          setUsers(data.users)
        } else {
          setFailedCreation(
            'Ocurrió un error inesperado. Contacte a soporte si el problema persiste.'
          )
          return setTimeout(() => setFailedCreation(false), 15000)
        }
      })
      .catch((error) => {
        if (error !== 'Fetch Aborted by User.') {
          console.error('Error:', error)
        }
      })
  }

  const sendForm = (event) => {
    event.preventDefault()
    const normalizedTitle = normalizeRouteTitle(title)

    if (normalizedTitle.length <= 0) {
      setTitle(normalizedTitle)
      setFailedCreation('Ingrese un nombre de ruta válido antes de guardar.')
      setSaving(false)
      return setTimeout(() => setFailedCreation(false), 15000)
    }

    if (normalizedTitle !== title) {
      setTitle(normalizedTitle)
    }

    if (users.length <= 0) {
      setFailedCreation('Asigne al menos un usuario antes de guardar.')
      setSaving(false)
      return setTimeout(() => setFailedCreation(false), 15000)
    }
    if (clients.length <= 0) {
      setFailedCreation('Seleccione al menos un cliente antes de guardar.')
      setSaving(false)
      return setTimeout(() => setFailedCreation(false), 15000)
    }
    for (let i = 0; i < clients.length; i++) {
      // Revisar que todos los campos en clients esten completos
      const currentClient = clients[i]
      if (currentClient.startDate == '') {
        setFailedCreation('Ingrese la fecha de inicio de ruta para cada cliente antes de guardar.')
        setSaving(false)
        return setTimeout(() => setFailedCreation(false), 15000)
      }
      if (!Array.isArray(currentClient.visitDays) || currentClient.visitDays.length <= 0) {
        setFailedCreation(
          'Seleccione al menos un día de visita para cada cliente antes de guardar.'
        )
        setSaving(false)
        return setTimeout(() => setFailedCreation(false), 15000)
      }
      const recurrence = Number(currentClient.recurrence)
      if (!Number.isFinite(recurrence) || recurrence < 7) {
        setFailedCreation('Defina una recurrencia válida (mínimo 7 días) para cada cliente.')
        setSaving(false)
        return setTimeout(() => setFailedCreation(false), 15000)
      }
    }

    setFailedCreation(false)
    setReadyToCollect(true)
  }

const collectData = () => {
  const normalizedTitle = normalizeRouteTitle(title)
  const newLogs = detectChangesLog(isEditing, baseClientsRef, baseUsersRef, clients, users)

  const jsonData = {
    edit: isEditing,
    route_plan_uid: routeUid,
    business_uid: cookie.load('business_tenant_uid'),
    title: normalizedTitle,
    clients: clients,
    users: users,
    client_uids: clients.map((client) => client.value),
    user_uids: users.map((user) => user.uid),
    changes_log: newLogs
  }
  postTaskToBackend(jsonData)
  setReadyToCollect(false)
}

  const postTaskToBackend = (body) => {

    setSaving(true)
    authFetch(`${BASE_BACKEND_URL}/route_plans`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
      .then((r) => evaluateResponse(r))
      .catch((error) => {
        console.error('Error saving route plan:', error)
        setFailedCreation(mapRoutePlanError({ code: 'route_plan.network_error' }))
        setSaving(false)
      })
  }

  const startCopying = async (uid) => {
    setCopyRoute(true)
    let routeToCopy = await getRoutePlanToCopy(uid)
    if (routeToCopy) {
      try {
        setRouteData(routeToCopy.route_plan_form_info)
        let routeClients = routeToCopy.route_plan_form_info.clients
        let routeUsers = routeToCopy.route_plan_form_info.users

        await getClientsForCopy(routeToCopy.client_uids, routeClients, false)

        setCopyRoute(false)

        baseClientsRef.current = routeClients || []
        baseUsersRef.current = routeUsers || []
      } catch (e) {
        console.log('Error en copia de plan de visitas: ', e)
        setFailedCreation(
          `Ocurrió un error inesperado al hacer copia del plan de visitas. . Contacte a soporte si el problema persiste.`
        )
        return setCopyRoute(false)
      }
    }
  }

  const startCopyForEditing = async (uid) => {
    setCopyRoute(true)
    let routeToEdit = await getRoutePlanToCopy(uid)
    if (routeToEdit) {
      try {
        setRouteUid(uid)
        setRouteData(routeToEdit.route_plan_form_info)
        setTitle(normalizeRouteTitle(routeToEdit.route_plan_form_info.title))

        let routeClients = routeToEdit.route_plan_form_info.clients
        let routeUsers = routeToEdit.route_plan_form_info.users

        await getClientsForCopy(routeToEdit.client_uids, routeClients, true)
        await getUsersForEdit(routeToEdit.user_uids, routeUsers)

        setCopyRoute(false)

        baseClientsRef.current = routeClients || []
        baseUsersRef.current = routeUsers || []
      } catch (e) {
        console.log('Error en edicion de plan de visitas: ', e)
        setFailedCreation(
          `Ocurrió un error inesperado al cargar data para editar el plan de visita. Contacte a soporte si el problema persiste.`
        )
        return setCopyRoute(false)
      }
    }
  }

  const getRoutePlanToCopy = async (uid) => {
    abortController = new AbortController()
    try {
      const response = await authFetch(`${BASE_BACKEND_URL}/route_plans/${uid}`, {
        method: 'GET',
        signal: abortController.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })
        if (!response) return

      if (response.status != 200) {
        return false
      } else {
        let json = await response.json()
        return json
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    }
  }

  const setCopyParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const uuidToCopy = urlParams.get('copy') || ''
    const uuidToEdit = urlParams.get('edit') || ''
    if (uuidToCopy) {
      startCopying(uuidToCopy)
    } else if (uuidToEdit) {
      setIsEditing(true)
      startCopyForEditing(uuidToEdit)
    }
  }

  useEffect(() => {
    setCopyParams()
  }, [])

  const genericSaveErrorMessage =
    'No fue posible guardar los cambios. Ocurrió un error inesperado. Contacte a soporte si el problema persiste.'

  const parseResponseJsonSafely = async (response) => {
    try {
      return await response.json()
    } catch (error) {
      return null
    }
  }

  const evaluateResponse = async (response) => {
    // navigate to task details
    switch (response.status) {
      case 404:
        let error_data = await parseResponseJsonSafely(response)

        if (error_data?.message == 'Used title') {
          setFailedCreation(
            'El nombre del plan de visitas ya existe. Ingrese un nombre diferente antes de guardar.'
          )
          setSaving(false)
          break
        } else if (error_data?.message == 'USERS OR CLIENTS ERROR') {
          setUnavailableUsers(error_data.unavailable_users)
          setUnavailableClients(error_data.unavailable_clients)

          setSaving(false)
          break
        } else {
          setFailedCreation(genericSaveErrorMessage)
          setSaving(false)
          break
        }
      case 422: {
        const error_data = await parseResponseJsonSafely(response)
        setFailedCreation(mapRoutePlanError(error_data || genericSaveErrorMessage))
        setSaving(false)
        break
      }
      case 400:
      case 500:
        setFailedCreation(genericSaveErrorMessage)
        setSaving(false)
        break
      default:
        setFailedCreation(false)
        // redirect to visitplan index
        window.location = `/plan_de_visitas?success=true`
        break
    }
  }

  const cancelCreate = (event) => {
    event.preventDefault()
    const val = window.confirm(
      '¿Está seguro que quiere continuar? Los cambios editados no seran guardados'
    )
    if (val) {
      window.location = '/plan_de_visitas'
    }
  }

  const openUsersPage = (event) => {
    event.preventDefault()
    window.open('/usuarios')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Delete') {
      e.preventDefault()
    }
  }

  const closeUnavailableModal = (confirmed) => {
    if (confirmed) {
      if (unavailableClients != null) {
        const remainingClients = clients.filter((client) => {
          return !unavailableClients.includes(client.value)
        })
        setClients(remainingClients)
      }
      if (unavailableUsers != null) {
        const remainingUsers = users.filter((user) => {
          return !unavailableUsers.includes(user.uid)
        })
        setUsers(remainingUsers)
      }
    }
    setShowUnavailableModal(false)
  }

  useEffect(() => {
    getUsers()
  }, [])

  useEffect(() => {
    if (unavailableClients != null || unavailableUsers != null) {
      setShowUnavailableModal(true)
    }
  }, [unavailableClients, unavailableUsers])

  useEffect(() => {
    if (isEditing && occupiedUsersData != null) {
      users.forEach((u) => delete occupiedUsersData[`${u.uid} - ${u.full_name}`])
    }
  }, [isEditing, users, userData])

  useEffect(() => {
    if (readyToCollect) {
      collectData()
    }
  }, [readyToCollect])

  return (
    <div>
      <div>
        <div>
          <>
            {failedCreation && (
              <div
                className="alert alert-danger d-flex justify-content-between align-items-center"
                role="alert"
              >
                <div>
                  <svg
                    width="19"
                    height="16"
                    viewBox="0 0 19 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="warning-route-plan-icon"
                  >
                    <path
                      d="M9.16667 0.166992L0 16.0003H18.3333L9.16667 0.166992Z"
                      fill="#FEC047"
                    />
                    <path
                      d="M9.16563 3.49219L15.4406 14.3339H2.89062L9.16563 3.49219Z"
                      fill="#FEC047"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.33398 6.83398H10.0007V10.1673H8.33398V6.83398ZM8.33398 11.834H10.0007V13.5007H8.33398V11.834Z"
                      fill="#202020"
                    />
                  </svg>
                  <strong>{failedCreation}</strong>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={(event) => handleCloseAlert(event)}
                  aria-label="Close"
                ></button>
              </div>
            )}
            {showUnavailableModal && (
              <UnavailableUserClientModal
                users={users}
                clients={clients}
                showModal={true}
                closeModal={closeUnavailableModal}
                unavailableClients={unavailableClients}
                unavailableUsers={unavailableUsers}
              />
            )}
            <div className="show-title">
              {isEditing ? <h2>Editar plan de visitas</h2> : <h2>Crear nuevo plan de visitas</h2>}
            </div>
            <form onSubmit={sendForm}>
              <div className="index-table">
                <div className="show-area">
                  <div className="create-promo-wrapper">
                    <div className="inline-block full-width">
                      <div className="float-left">
                        {isEditing ? (
                          <h5>Complete los datos para editar el plan de visitas:</h5>
                        ) : (
                          <h5>Ingrese los siguientes datos para crear un nuevo plan de visitas:</h5>
                        )}
                      </div>
                      <div className="float-right">
                        <button
                          disabled={saving || copyRoute}
                          onClick={cancelCreate}
                          className="cancel-button"
                        >
                          Cancelar
                        </button>
                        <input
                          disabled={saving || copyRoute}
                          className="save-button"
                          value={saving ? 'Guardando...' : 'Guardar'}
                          type="submit"
                        />
                      </div>
                    </div>
                    <hr />
                    {saving && (
                      <div className="td-centered">
                        <div className="saving-route-box">
                          <MetroSpinner size={24} color="#747474" />
                          <span className="saving-route-box-title">
                            Estamos validando tu plan de visitas…
                          </span>
                          <span className="saving-route-box-subtitle">
                            Consulta el estado del plan en la columna “Estado” de la tabla
                            principal.
                          </span>
                        </div>
                      </div>
                    )}
                    {copyRoute && !saving && (
                      <div className="td-centered">
                        <div className="saving-route-box">
                          <MetroSpinner size={24} color="#747474" />
                          <span className="saving-route-box-title">
                            Cargando plan de visitas...
                          </span>
                        </div>
                      </div>
                    )}
                    {!copyRoute && !saving && (
                      <>
                        <div className="promo-form-section mt-33">
                          <div className="title-promos-form">
                            <b className="bold-step">PASO 1: Escriba el nombre de la ruta</b>
                          </div>
                          <hr className="hr-promos-form" />
                          <div className="form-group">
                            <input
                              type="text"
                              maxLength="100"
                              className="form-control"
                              id="title"
                              name="title"
                              onChange={handleTitleChange}
                              value={title}
                              placeholder="Ejemplo: Ruta Norte"
                              required
                            />
                            <p className="small text-muted mt-10 pull-right">
                              Cantidad máxima de caracteres {Math.abs(title.length - 100)}/100
                            </p>
                          </div>
                        </div>
                        <div className="promo-form-section mt-33">
                          <div className="title-promos-form">
                            <b className="bold-step">Paso 2: Asigne los usuarios a esta ruta</b>
                            <button
                              disabled={saving}
                              onClick={(e) => openUsersPage(e)}
                              type="submit"
                              className="ubiqua-secondary-btn float-right"
                              style={{ marginTop: '-10px' }}
                            >
                              Ver planes de visitas asignados
                            </button>
                          </div>
                          <p className="b-text-price margint-5">
                            <span className="b-text-bold">Nota: </span>
                            Solo es posible asignar un usuario por ruta. Si un usuario ya está
                            asignado a esta ruta, no podrá seleccionarse nuevamente.
                          </p>
                          <hr className="hr-promos-form-price" />
                        </div>
                        <div className="promo-form-section">
                          <div className="fake-validator-container">
                            <CheckboxDropdownList
                              className="mr-10"
                              users={users}
                              isEditing={isEditing}
                              options={userOptions || []}
                              onSelectionChange={updateSelectedUsers}
                              disabled={title.length <= 0}
                              occupiedUsersData={occupiedUsersData}
                            />
                          </div>
                          {users && users.length > 0 && (
                            <Table responsive className="mt-33">
                              <thead className="table-header-gray">
                                <tr>
                                  <th className="vertical-align-middle td-text-limited-space">#</th>
                                  <th className="vertical-align-middle">CÓDIGO</th>
                                  <th className="vertical-align-middle">USUARIO</th>
                                  <th className="align-center-table-column">ELIMINAR</th>
                                </tr>
                              </thead>
                              <tbody>
                                {users.map((user, index) => (
                                  <tr key={index}>
                                    <td className="td-text-limited td-text-limited-space">
                                      {index + 1}
                                    </td>
                                    <td className="td-text-limited">{user.uid}</td>
                                    <td className="td-text-limited">{user.full_name}</td>
                                    <td className="td-text-limited td-text-limited-center align-center-table-column">
                                      <button
                                        type="button"
                                        className={`delete-report-btn`}
                                        style={{ height: '35px', marginLeft: '0px' }}
                                        onClick={(e) => {
                                          handleDeleteUser(user.uid)
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faTrashAlt} style={{ margin: 0 }} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                        <div className="promo-form-section mt-33">
                          <div className="title-promos-form">
                            <b className="bold-step">
                              Paso 3: Escoja los clientes que aplican a esta ruta y defina la
                              recurrencia de las visitas
                            </b>
                          </div>
                          <hr className="hr-promos-form-price" />
                        </div>
                        <div className="promo-form-section">
                          <div className="fake-validator-container">
                            <input
                              className={`input-fake-validator ${(users.length <= 0 || title.length <= 0) && 'inactive'}`}
                              type="text"
                              disabled={users.length <= 0 || title.length <= 0}
                            />
                            <AsyncPaginate
                              className={`clients ${(users.length <= 0 || title.length <= 0) && 'inactive'} `}
                              placeholder={'Buscar cliente(s) por nombre o código'}
                              noOptionsMessage={() => 'No hay resultados'}
                              loadingMessage={() => 'Cargando clientes...'}
                              name="selectOptionClients"
                              isDisabled={users.length <= 0 || title.length <= 0}
                              loadOptions={getClient}
                              additional={{ page: 1 }}
                              isSearchable={true}
                              isMulti
                              value={clients}
                              onChange={handleClientOption}
                              onBlur={abortClientFetchs}
                              onKeyDown={(e) => handleKeyDown(e)}
                              controlShouldRenderValue={false}
                              isClearable={false}
                              backspaceRemovesValue={false}
                            />
                          </div>
                          {clients && clients.length > 0 && (
                            <Table responsive className="mt-33">
                              <thead className="table-header-gray">
                                <tr>
                                  <th className="vertical-align-middle td-text-limited-space">#</th>
                                  <th className="vertical-align-middle">CLIENTE</th>
                                  <th className="vertical-align-middle">
                                    <div
                                      className="route-plan-table-container"
                                      title={
                                        'Esta es la fecha a partir de la cual el cliente comenzará a recibir visitas como parte de esta ruta.'
                                      }
                                    >
                                      <span className="th-header-text">
                                        FECHA DE INICIO EN LA RUTA
                                      </span>
                                      <FontAwesomeIcon icon={faQuestionCircle} />
                                    </div>
                                  </th>
                                  <th className="vertical-align-middle">
                                    <div
                                      className="route-plan-table-container"
                                      title={
                                        'Aquí se definen los días específicos de la semana en los que se realizarán las visitas a este cliente.'
                                      }
                                    >
                                      <span className="th-header-text">
                                        ESCOJA LOS DÍA(S) DE VISITA
                                      </span>
                                      <FontAwesomeIcon icon={faQuestionCircle} />
                                    </div>
                                  </th>
                                  <th className="vertical-align-middle">
                                    <div
                                      className="route-plan-table-container"
                                      title={
                                        'Indica cada cuántas semanas o meses se realizarán las visitas. Puedes ajustar el número y seleccionar el tipo de frecuencia según la necesidad del cliente..'
                                      }
                                    >
                                      <span className="th-header-text">RECURRENCIA</span>
                                      <FontAwesomeIcon icon={faQuestionCircle} />
                                    </div>
                                  </th>
                                  <th className="align-center-table-column">ACCIÓN</th>
                                </tr>
                              </thead>
                              <tbody>
                                <ActionButtonProvider>
                                  {clients.map((client, index) => (
                                    <ClientsInRoutePlanList
                                      key={client.value}
                                      client={client}
                                      clientIndex={index}
                                      handleDeleteClient={handleDeleteClient}
                                      handleChangeStartDate={handleChangeStartDate}
                                      handleChangeVisitDays={handleChangeVisitDays}
                                      handleChangeRecurrence={handleChangeRecurrence}
                                    />
                                  ))}
                                </ActionButtonProvider>
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </>
        </div>
      </div>
    </div>
  )
}
export default CreateNewRoutePlan
